// ── Replay Recorder ──
// Records game snapshots during live gameplay for later playback.
// Captures player states, round info, bomb state, grenades, and events each tick.

import { type PlayerSnapshot, type GameEvent } from '../net/messages';
import { TICK_RATE } from '../core/config';

const DEFAULT_MAX_REPLAY_FRAMES = TICK_RATE * 60 * 10;

// ── Types ──

export interface PlayerInfo {
  id: number;
  name: string;
  team: number;
}

export interface BombSnapshot {
  isPlanted: boolean;
  isDetonated: boolean;
  isDefused: boolean;
  isPlanting: boolean;
  isDefusing: boolean;
  position?: { x: number; y: number; z: number };
  plantProgress: number;
  defuseProgress: number;
  timer: number;
  planterId: number;
  defuserId: number;
  plantSite: string;
}

export interface GrenadeSnapshot {
  id: number;
  type: string;
  ownerId: number;
  x: number;
  y: number;
  z: number;
}

export interface ZoneSnapshot {
  id: number;
  x: number;
  y: number;
  z: number;
  radius: number;
  timer: number;
}

export interface ReplayFrame {
  tick: number;
  timestamp: number;
  players: Map<number, PlayerSnapshot>;
  roundPhase: string;
  roundNumber: number;
  teamScores: number[];
  timeRemaining: number;
  bomb: BombSnapshot | null;
  activeGrenades: GrenadeSnapshot[];
  smokeZones: ZoneSnapshot[];
  fireZones: ZoneSnapshot[];
  events: GameEvent[];
}

export interface ReplayData {
  version: number;
  map: string;
  date: string;
  duration: number;
  tickRate: number;
  totalTicks: number;
  frames: ReplayFrame[];
  metadata: {
    players: PlayerInfo[];
    finalScore: number[];
  };
}

// ── Snapshot input interface (what recordFrame receives) ──

export interface SnapshotInput {
  tick: number;
  players: PlayerSnapshot[];
  roundPhase?: string;
  roundNumber?: number;
  teamScores?: number[];
  timeRemaining?: number;
  bomb?: any;
  activeGrenades?: any[];
  smokeZones?: any[];
  fireZones?: any[];
}

// ── Recorder ──

export class ReplayRecorder {
  private frames: ReplayFrame[] = [];
  private frameStart = 0;
  private recording = false;
  private mapName = '';
  private startTime = 0;
  private seenPlayers = new Map<number, PlayerInfo>();

  constructor(private readonly maxFrames: number = DEFAULT_MAX_REPLAY_FRAMES) {}

  /** Start recording a new replay. Clears any previous data. */
  startRecording(map: string): void {
    this.frames = [];
    this.frameStart = 0;
    this.seenPlayers.clear();
    this.mapName = map;
    this.startTime = Date.now();
    this.recording = true;
  }

  /** Record a single frame from the current game state. */
  recordFrame(snapshot: SnapshotInput, events: GameEvent[]): void {
    if (!this.recording) return;

    // Build player map
    const playerMap = new Map<number, PlayerSnapshot>();
    for (const p of snapshot.players) {
      playerMap.set(p.id, { ...p });

      // Track unique players seen
      if (!this.seenPlayers.has(p.id)) {
        this.seenPlayers.set(p.id, {
          id: p.id,
          name: `Player ${p.id}`,
          team: p.team ?? 0,
        });
      }
    }

    // Build bomb snapshot
    let bomb: BombSnapshot | null = null;
    if (snapshot.bomb) {
      const b = snapshot.bomb;
      bomb = {
        isPlanted: b.isPlanted ?? false,
        isDetonated: b.isDetonated ?? false,
        isDefused: b.isDefused ?? false,
        isPlanting: b.isPlanting ?? false,
        isDefusing: b.isDefusing ?? false,
        position: b.position,
        plantProgress: b.plantProgress ?? 0,
        defuseProgress: b.defuseProgress ?? 0,
        timer: b.timer ?? 0,
        planterId: b.planterId ?? 0,
        defuserId: b.defuserId ?? 0,
        plantSite: b.plantSite ?? '',
      };
    }

    // Build grenade snapshots
    const activeGrenades: GrenadeSnapshot[] = (snapshot.activeGrenades ?? []).map((g: any) => ({
      id: g.id,
      type: g.type,
      ownerId: g.ownerId,
      x: g.x,
      y: g.y,
      z: g.z,
    }));

    // Build zone snapshots
    const smokeZones: ZoneSnapshot[] = (snapshot.smokeZones ?? []).map((z: any) => ({
      id: z.id,
      x: z.x,
      y: z.y,
      z: z.z,
      radius: z.radius,
      timer: z.timer,
    }));

    const fireZones: ZoneSnapshot[] = (snapshot.fireZones ?? []).map((z: any) => ({
      id: z.id,
      x: z.x,
      y: z.y,
      z: z.z,
      radius: z.radius,
      timer: z.timer,
    }));

    const frame: ReplayFrame = {
      tick: snapshot.tick,
      timestamp: Date.now() - this.startTime,
      players: playerMap,
      roundPhase: snapshot.roundPhase ?? 'active',
      roundNumber: snapshot.roundNumber ?? 1,
      teamScores: snapshot.teamScores ? [...snapshot.teamScores] : [0, 0, 0],
      timeRemaining: snapshot.timeRemaining ?? 0,
      bomb,
      activeGrenades,
      smokeZones,
      fireZones,
      events: [...events],
    };

    if (this.maxFrames <= 0) {
      return;
    }

    if (this.frames.length < this.maxFrames) {
      this.frames.push(frame);
      return;
    }

    this.frames[this.frameStart] = frame;
    this.frameStart = (this.frameStart + 1) % this.maxFrames;
  }

  /** Stop recording and return the completed replay data. */
  stopRecording(): ReplayData {
    this.recording = false;

    const endTime = Date.now();
    const durationMs = endTime - this.startTime;
    const frames = this.getFramesInOrder();

    // Get final score from last frame
    const lastFrame = frames[frames.length - 1];
    const finalScore = lastFrame ? [...lastFrame.teamScores] : [0, 0, 0];

    const replay: ReplayData = {
      version: 1,
      map: this.mapName,
      date: new Date().toISOString(),
      duration: durationMs / 1000,
      tickRate: TICK_RATE,
      totalTicks: frames.length,
      frames,
      metadata: {
        players: Array.from(this.seenPlayers.values()),
        finalScore,
      },
    };

    this.frames = [];
    this.frameStart = 0;
    this.seenPlayers.clear();

    return replay;
  }

  /** Whether we are currently recording. */
  isRecording(): boolean {
    return this.recording;
  }

  private getFramesInOrder(): ReplayFrame[] {
    if (this.frameStart === 0) {
      return [...this.frames];
    }

    return [
      ...this.frames.slice(this.frameStart),
      ...this.frames.slice(0, this.frameStart),
    ];
  }
}
